<template>
  <div class="wa-wizard">
    <!-- ══════════════════════════════════════════
         Pantalla de selección de método
    ══════════════════════════════════════════ -->
    <div v-if="!method" class="wa-method-select">
      <div class="wa-wizard__step-title mb-1">
        Conecta tu cuenta de WhatsApp Business
      </div>
      <div class="wa-wizard__step-desc mb-5">
        Elige cómo quieres configurar WhatsApp. Si no sabes cuál elegir, usa la
        opción recomendada.
      </div>

      <div class="wa-method-cards">
        <!-- Opción A — Embedded Signup (OAuth) -->
        <div
          class="wa-method-card wa-method-card--primary"
          @click="method = 'oauth'"
        >
          <div class="wa-method-card__badge">Recomendado</div>
          <div class="wa-method-card__icon">
            <i
              class="el-icon-connection"
              style="font-size:28px;color:#3ecfb4;"
            ></i>
          </div>
          <div class="wa-method-card__title">Conectar con Meta</div>
          <div class="wa-method-card__desc">
            Inicia sesión con tu cuenta de Facebook Business y autoriza Mydessk
            en segundos. No necesitas crear una app de desarrolladores.
          </div>
          <div class="wa-method-card__action">
            Conectar con Meta <i class="el-icon-arrow-right"></i>
          </div>
        </div>

        <!-- Opción B — Credenciales manuales -->
        <div class="wa-method-card" @click="method = 'manual'">
          <div class="wa-method-card__icon">
            <i class="el-icon-key" style="font-size:28px;color:#6b7280;"></i>
          </div>
          <div class="wa-method-card__title">Credenciales manuales</div>
          <div class="wa-method-card__desc">
            Ingresa directamente el <strong>Phone Number ID</strong>,
            <strong>Access Token</strong> y <strong>WABA ID</strong> desde Meta
            for Developers. Para usuarios avanzados.
          </div>
          <div class="wa-method-card__action" style="color:#6b7280;">
            Configurar manualmente <i class="el-icon-arrow-right"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════
         FLUJO A — Embedded Signup (OAuth)
    ══════════════════════════════════════════ -->
    <div v-else-if="method === 'oauth'" class="wa-wizard__step">
      <div class="d-flex align-items-center mb-4" style="gap:8px;">
        <el-button
          type="text"
          icon="el-icon-back"
          style="padding:0;color:#6b7280;"
          @click="method = null"
        >
          Volver
        </el-button>
        <span style="font-size:12px;color:#9ca3af;">Conectar con Meta</span>
      </div>

      <div class="wa-wizard__step-title mb-1">Conecta con Meta en segundos</div>
      <div class="wa-wizard__step-desc mb-4">
        Serás redirigido a Meta para autorizar Mydessk. Al finalizar, regresarás
        aquí con tu cuenta configurada automáticamente.
      </div>

      <!-- Guía rápida -->
      <div class="wa-guide mb-4">
        <div class="wa-guide__step">
          <span class="wa-guide__num">1</span>
          <span
            >Inicia sesión con tu
            <strong>cuenta de Facebook Business</strong></span
          >
        </div>
        <div class="wa-guide__step">
          <span class="wa-guide__num">2</span>
          <span
            >Selecciona o crea tu
            <strong>cuenta de WhatsApp Business</strong> (WABA)</span
          >
        </div>
        <div class="wa-guide__step">
          <span class="wa-guide__num">3</span>
          <span
            >Elige el <strong>número de teléfono</strong> que quieres
            conectar</span
          >
        </div>
        <div class="wa-guide__step">
          <span class="wa-guide__num">4</span>
          <span>Regresa aquí — Mydessk configura todo automáticamente ✓</span>
        </div>
      </div>

      <!-- Teléfono de display (opcional) -->
      <el-form label-position="top" size="small" class="mb-4">
        <el-form-item
          label="Número de teléfono (opcional — solo para referencia)"
        >
          <el-input
            v-model="oauthPhone"
            placeholder="Ej: +593 99 123 4567"
            clearable
          />
          <div style="font-size:11px;color:#9ca3af;margin-top:4px;">
            Si lo dejas vacío, se detectará automáticamente durante el proceso
            de Meta.
          </div>
        </el-form-item>
      </el-form>

      <div v-if="oauthError" class="wa-alert wa-alert--error mb-3">
        <i class="el-icon-close-circle mr-1"></i> {{ oauthError }}
      </div>

      <el-button
        :loading="loadingOauthUrl"
        style="background:#3ecfb4;color:#fff;border:none;border-radius:8px;font-weight:600;min-width:200px;"
        @click="startOAuth"
      >
        <i class="el-icon-connection mr-1"></i>
        Conectar con Meta
      </el-button>

      <div style="font-size:11px;color:#9ca3af;margin-top:12px;">
        Serás redirigido a facebook.com. Es seguro — Mydessk solo solicita
        permisos de mensajería de WhatsApp Business.
      </div>
    </div>

    <!-- ══════════════════════════════════════════
         FLUJO B — Credenciales manuales
    ══════════════════════════════════════════ -->
    <div v-else-if="method === 'manual'">
      <!-- Back link en el top -->
      <div class="d-flex align-items-center mb-4" style="gap:8px;">
        <el-button
          type="text"
          icon="el-icon-back"
          style="padding:0;color:#6b7280;"
          @click="resetManual"
        >
          Volver
        </el-button>
        <span style="font-size:12px;color:#9ca3af;">Credenciales manuales</span>
      </div>

      <el-steps :active="step" finish-status="success" simple class="mb-4">
        <el-step title="Credenciales" icon="el-icon-key" />
        <el-step title="Verificar conexión" icon="el-icon-connection" />
        <el-step title="Auto-respuesta" icon="el-icon-chat-dot-round" />
      </el-steps>

      <!-- ── Paso 0: Ingresar credenciales ── -->
      <div v-if="step === 0" class="wa-wizard__step">
        <div class="wa-wizard__step-title">
          Ingresa las credenciales de Meta
        </div>
        <div class="wa-wizard__step-desc mb-4">
          Obtén estas credenciales en
          <a
            href="https://developers.facebook.com/apps/"
            target="_blank"
            rel="noopener"
            style="color:#3ecfb4;text-decoration:none;font-weight:600;"
          >
            Meta for Developers
            <i class="el-icon-top-right" style="font-size:11px;"></i>
          </a>
        </div>

        <!-- Guía visual -->
        <div class="wa-guide mb-4">
          <div class="wa-guide__step">
            <span class="wa-guide__num">1</span>
            <span
              >Selecciona tu app → <strong>WhatsApp</strong> →
              <strong>Configuración de la API</strong></span
            >
          </div>
          <div class="wa-guide__step">
            <span class="wa-guide__num">2</span>
            <span
              >Copia el <strong>Identificador de número de teléfono</strong> y
              el <strong>Token de acceso</strong></span
            >
          </div>
          <div class="wa-guide__step">
            <span class="wa-guide__num">3</span>
            <span
              >Para un token permanente:
              <strong>Configuración del negocio</strong> →
              <strong>Usuarios del sistema</strong> → generar token con permisos
              <code>whatsapp_business_messaging</code></span
            >
          </div>
        </div>

        <el-form label-position="top" size="small">
          <el-form-item
            label="Identificador de número de teléfono (Phone Number ID)"
            required
          >
            <el-input
              v-model="form.phone_number_id"
              placeholder="Ej: 179356828587180"
              clearable
            />
          </el-form-item>

          <el-form-item label="Token de acceso (Access Token)" required>
            <el-input
              v-model="form.access_token"
              type="textarea"
              :rows="3"
              placeholder="EAABsbCS..."
            />
            <div style="font-size:11px;color:#9ca3af;margin-top:4px;">
              Usa un token de usuario del sistema (permanente) para no tener que
              reconectar cada 60 días.
            </div>
          </el-form-item>

          <el-form-item
            label="ID de cuenta de WhatsApp Business (WABA ID)"
            required
          >
            <el-input
              v-model="form.waba_id"
              placeholder="Ej: 154355887768370"
              clearable
            />
            <div style="font-size:11px;color:#9ca3af;margin-top:4px;">
              Encuéntralo en Meta Business Manager → Cuentas → Cuentas de
              WhatsApp Business.
            </div>
          </el-form-item>

          <el-form-item label="Número de teléfono (con código de país)">
            <el-input
              v-model="form.display_phone"
              placeholder="Ej: +593 99 123 4567"
              clearable
            />
          </el-form-item>
        </el-form>

        <div v-if="saveError" class="wa-alert wa-alert--error mb-3">
          <i class="el-icon-close-circle mr-1"></i> {{ saveError }}
        </div>

        <el-button
          :loading="saving"
          :disabled="!canSave"
          style="background:#3ecfb4;color:#fff;border:none;border-radius:8px;font-weight:600;"
          @click="saveCredentials"
        >
          Guardar y continuar
        </el-button>
      </div>

      <!-- ── Paso 1: Verificar conexión ── -->
      <div v-if="step === 1" class="wa-wizard__step">
        <div class="wa-wizard__step-title">Verificando conexión con Meta</div>
        <div class="wa-wizard__step-desc mb-4">
          Comprobando que las credenciales sean válidas y que el número pueda
          enviar mensajes.
        </div>

        <div v-if="testing" class="text-center py-4">
          <i class="el-icon-loading" style="font-size:28px;color:#3ecfb4;"></i>
          <div style="font-size:13px;color:#6b7280;margin-top:8px;">
            Verificando...
          </div>
        </div>

        <div v-else-if="testResult">
          <!-- Éxito -->
          <div
            v-if="testResult.success"
            class="wa-alert wa-alert--success mb-4"
          >
            <i class="el-icon-circle-check mr-1" style="font-size:16px;"></i>
            <strong>¡Conexión exitosa!</strong> El número está listo para enviar
            mensajes.
          </div>

          <!-- Error -->
          <div v-else>
            <div class="wa-alert wa-alert--error mb-3">
              <i class="el-icon-close-circle mr-1"></i>
              {{ testResult.message || "No se pudo verificar la conexión." }}
            </div>
            <div style="font-size:12px;color:#6b7280;margin-bottom:12px;">
              Revisa que el <strong>Token de acceso</strong> tenga permisos
              <code>whatsapp_business_messaging</code> y que el
              <strong>Phone Number ID</strong> sea el correcto.
            </div>
            <el-button size="small" plain @click="step = 0"
              >← Corregir credenciales</el-button
            >
          </div>

          <el-button
            v-if="testResult.success"
            style="background:#2d2b55;color:#fff;border:none;border-radius:8px;"
            @click="step = 2"
          >
            Continuar
          </el-button>
        </div>
      </div>

      <!-- ── Paso 2: Auto-respuesta ── -->
      <div v-if="step === 2" class="wa-wizard__step">
        <div class="wa-wizard__step-title">Configura tu auto-respuesta</div>
        <div class="wa-wizard__step-desc mb-4">
          Cuando un cliente te escriba, recibirá este mensaje automáticamente.
          Puedes cambiarlo más tarde.
        </div>

        <div class="d-flex align-items-center mb-3">
          <el-switch v-model="autoReplyEnabled" active-color="#3ecfb4" />
          <span style="margin-left:10px;font-size:13px;color:#374151;">
            {{ autoReplyEnabled ? "Activa" : "Inactiva" }}
          </span>
        </div>

        <el-input
          v-if="autoReplyEnabled"
          v-model="autoReplyMessage"
          type="textarea"
          :rows="3"
          placeholder="Gracias por tu mensaje. Un agente te contactará pronto."
          class="mb-3"
        />

        <el-button
          :loading="finishing"
          style="background:#2d2b55;color:#fff;border:none;border-radius:8px;"
          @click="finish"
        >
          Finalizar configuración
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import configService from "../../../services/configService";

export default {
  name: "WhatsappSetupWizard",
  props: {
    platformStatus: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      // Selección de método: null | 'oauth' | 'manual'
      method: null,

      // ── OAuth ──
      oauthPhone: "",
      loadingOauthUrl: false,
      oauthError: null,
      fbSessionInfo: null,
      fbMessageHandler: null,

      // ── Manual — stepper ──
      step: 0,
      form: {
        phone_number_id: "",
        access_token: "",
        waba_id: "",
        display_phone: ""
      },
      saving: false,
      saveError: null,
      testing: false,
      testResult: null,
      autoReplyEnabled: true,
      autoReplyMessage:
        "Gracias por tu mensaje. Un agente de nuestro equipo te contactará pronto.",
      finishing: false
    };
  },
  computed: {
    canSave() {
      return (
        this.form.phone_number_id.trim() &&
        this.form.access_token.trim() &&
        this.form.waba_id.trim()
      );
    }
  },
  beforeDestroy() {
    if (this.fbMessageHandler) {
      window.removeEventListener("message", this.fbMessageHandler);
    }
  },
  methods: {
    // ── OAuth (FB SDK — Embedded Signup) ──────────────────
    async startOAuth() {
      this.loadingOauthUrl = true;
      this.oauthError = null;
      this.fbSessionInfo = null;

      const appId = this.platformStatus && this.platformStatus.meta_app_id;
      const configId =
        this.platformStatus && this.platformStatus.meta_config_id;

      if (!appId) {
        this.oauthError =
          "No se pudo obtener la configuración de Meta. Contacta al soporte de Mydessk.";
        this.loadingOauthUrl = false;
        return;
      }

      try {
        await this._loadFbSdk(appId);
      } catch {
        this.oauthError =
          "No se pudo cargar el SDK de Meta. Verifica tu conexión e intenta de nuevo.";
        this.loadingOauthUrl = false;
        return;
      }

      // Escuchar el evento WA_EMBEDDED_SIGNUP que trae waba_id y phone_number_id
      if (this.fbMessageHandler) {
        window.removeEventListener("message", this.fbMessageHandler);
      }
      this.fbMessageHandler = event => {
        if (
          event.origin !== "https://www.facebook.com" &&
          event.origin !== "https://web.facebook.com"
        )
          return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === "WA_EMBEDDED_SIGNUP" && data.event === "FINISH") {
            this.fbSessionInfo = data.data || {};
          }
        } catch {
          /* ignorar mensajes no JSON */
        }
      };
      window.addEventListener("message", this.fbMessageHandler);

      const loginParams = {
        response_type: "code",
        override_default_response_type: true,
        extras: {
          feature: "whatsapp_embedded_signup",
          sessionInfoVersion: 2
        }
      };
      if (configId) {
        loginParams.config_id = configId;
      }

      window.FB.login(response => {
        window.removeEventListener("message", this.fbMessageHandler);
        this.fbMessageHandler = null;

        if (response.authResponse && response.authResponse.code) {
          this._exchangeOAuthCode(response.authResponse.code);
        } else {
          // Usuario canceló o cerró el popup
          this.oauthError =
            response.status === "not_authorized"
              ? "No autorizaste Mydessk en Meta. Intenta de nuevo."
              : "Inicio de sesión cancelado. Intenta de nuevo.";
          this.loadingOauthUrl = false;
        }
      }, loginParams);
    },

    _loadFbSdk(appId) {
      return new Promise((resolve, reject) => {
        // Si ya está cargado e inicializado, reinicializar con el appId correcto
        if (window.FB) {
          window.FB.init({
            appId,
            cookie: true,
            xfbml: false,
            version: "v19.0"
          });
          return resolve();
        }
        window.fbAsyncInit = () => {
          window.FB.init({
            appId,
            cookie: true,
            xfbml: false,
            version: "v19.0"
          });
          resolve();
        };
        const existing = document.getElementById("facebook-jssdk");
        if (existing) {
          // Script ya insertado pero FB aún no disponible — esperar fbAsyncInit
          return;
        }
        const script = document.createElement("script");
        script.id = "facebook-jssdk";
        script.src = "https://connect.facebook.net/en_US/sdk.js";
        script.onerror = () => reject(new Error("FB SDK load error"));
        document.head.appendChild(script);
      });
    },

    async _exchangeOAuthCode(code) {
      try {
        const res = await configService.connectWhatsAppOAuth({
          code,
          phone: this.oauthPhone.trim() || null,
          waba_id: (this.fbSessionInfo && this.fbSessionInfo.waba_id) || null,
          phone_number_id:
            (this.fbSessionInfo && this.fbSessionInfo.phone_number_id) || null
        });

        if (res.data && res.data.success) {
          this.$notifications.success({
            message: res.data.message || "¡WhatsApp conectado correctamente!"
          });
          this.$emit("connected");
        } else {
          this.oauthError =
            res.data?.message ||
            "No se pudo completar la conexión con Meta. Intenta de nuevo.";
        }
      } catch (err) {
        const apiMsg = err?.response?.data?.message;
        this.oauthError =
          apiMsg ||
          "Error al conectar con Meta. Verifica tu sesión e intenta de nuevo.";
      } finally {
        this.loadingOauthUrl = false;
      }
    },

    // ── Manual ─────────────────────────────────────────────
    resetManual() {
      this.method = null;
      this.step = 0;
      this.form = {
        phone_number_id: "",
        access_token: "",
        waba_id: "",
        display_phone: ""
      };
      this.saveError = null;
      this.testResult = null;
    },

    async saveCredentials() {
      this.saving = true;
      this.saveError = null;
      try {
        const res = await configService.upsertWhatsAppConfig({
          phone_number_id: this.form.phone_number_id.trim(),
          access_token: this.form.access_token.trim(),
          waba_id: this.form.waba_id.trim(),
          display_phone: this.form.display_phone.trim() || null,
          is_active: true
        });

        if (res.data && res.data.success) {
          this.step = 1;
          this.runTest();
        } else {
          this.saveError =
            res.data?.message || "Error al guardar las credenciales.";
        }
      } catch {
        this.saveError =
          "Error al guardar. Verifica tu sesión e intenta de nuevo.";
      } finally {
        this.saving = false;
      }
    },

    async runTest() {
      this.testing = true;
      this.testResult = null;
      try {
        const res = await configService.testWhatsAppConfig();
        this.testResult = {
          success: !!(res.data && res.data.success),
          message: res.data?.message || ""
        };
      } catch {
        this.testResult = {
          success: false,
          message: "No se pudo verificar la conexión."
        };
      } finally {
        this.testing = false;
      }
    },

    async finish() {
      this.finishing = true;
      try {
        if (this.autoReplyEnabled) {
          try {
            await configService.updateWhatsAppAutoReply(
              {
                auto_reply_enabled: true,
                auto_reply_message: this.autoReplyMessage
              },
              { skipErrorNotification: true }
            );
          } catch {
            // No crítico — se puede configurar después
          }
        }
        this.$notifications.success({
          message: "¡WhatsApp conectado correctamente!"
        });
        this.$emit("connected");
      } finally {
        this.finishing = false;
      }
    }
  }
};
</script>

<style scoped>
.wa-wizard {
  width: 100%;
}

/* ── Selección de método ── */
.wa-method-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 600px) {
  .wa-method-cards {
    grid-template-columns: 1fr;
  }
}
.wa-method-card {
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fff;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.wa-method-card:hover {
  border-color: #3ecfb4;
  box-shadow: 0 4px 16px rgba(62, 207, 180, 0.12);
}
.wa-method-card--primary {
  border-color: #3ecfb4;
  background: linear-gradient(135deg, #f0fdfb 0%, #fff 100%);
}
.wa-method-card__badge {
  position: absolute;
  top: -1px;
  right: 14px;
  background: #3ecfb4;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 0 0 6px 6px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.wa-method-card__icon {
  margin-bottom: 4px;
}
.wa-method-card__title {
  font-size: 15px;
  font-weight: 700;
  color: #2d2b55;
}
.wa-method-card__desc {
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.55;
  flex: 1;
}
.wa-method-card__action {
  font-size: 12px;
  font-weight: 600;
  color: #3ecfb4;
  margin-top: 4px;
}

/* ── Stepper (manual) ── */
.wa-wizard >>> .el-steps {
  width: 100%;
}
.wa-wizard >>> .el-step__title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
}
.wa-wizard >>> .el-step__title.is-success {
  color: #2d2b55 !important;
}
.wa-wizard >>> .el-step__head.is-success .el-step__icon {
  border-color: #2d2b55 !important;
  color: #2d2b55 !important;
}
.wa-wizard >>> .el-step__title.is-process {
  color: #3ecfb4 !important;
  font-weight: 700;
}
.wa-wizard >>> .el-step__head.is-process .el-step__icon {
  border-color: #3ecfb4 !important;
  color: #3ecfb4 !important;
}

.wa-wizard__step-title {
  font-size: 16px;
  font-weight: 700;
  color: #2d2b55;
  margin-bottom: 6px;
}
.wa-wizard__step-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
}

/* Guía de pasos */
.wa-guide {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.wa-guide__step {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}
.wa-guide__num {
  background: #2d2b55;
  color: #fff;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}

.wa-alert {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.wa-alert--warning {
  background: #fef3c7;
  color: #92400e;
}
.wa-alert--error {
  background: #fee2e2;
  color: #991b1b;
}
.wa-alert--success {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}
</style>
